import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useAppDeployment from '../../hooks/useAppDeployment';
import * as cardTemplatesService from '../../services/cardTemplatesService';
import AppDeploymentsService from '../../services/appDeploymentsService';

/**
 * הוק הלוגיקה של דף סטטוס האפליקציה
 * מתממשק עם useAppDeployment להתנהלות התהליך
 * וניהול המצב, התבניות, והתקדמות המשתמש
 */
const useAppStatusLogic = () => {
  const { fileId } = useParams();
  console.log('AppStatusLogic initialized with fileId:', fileId); // LOG
  const [showWelcomeCard, setShowWelcomeCard] = useState(true);
  const [error, setError] = useState('');
  const [cardIndex, setCardIndex] = useState(0);
  const [cardResponses, setCardResponses] = useState({});
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const [deploymentInitialized, setDeploymentInitialized] = useState(false);
  
  // שימוש בהוק המרכזי לניהול תהליך הדיפלוי
  const {
    deployStatus,
    cardTemplates,
    currentCardIndex, // Note: This is the index from useAppDeployment
    isLoading,
    formData,
    isSaving,
    lastSavedAt,
    startDeployment,
    goToNextCard,
    goToPrevCard,
    saveTemporaryData,
    saveAnswer
  } = useAppDeployment(fileId);

  // אפקט חדש: יצירת אובייקט Firebase בכניסה לעמוד
  useEffect(() => {
    console.log('Auto-initialization useEffect triggered');
    
    // פעל רק אם יש fileId וטרם נוצר deployStatus
    const autoInitializeDeployment = async () => {
      if (!fileId || deploymentInitialized) {
        console.log('Skipping auto-initialization: no fileId or already initialized');
        return;
      }
      
      try {
        console.log('Auto-initializing deployment for fileId:', fileId);
        
        // אם אין כרטיסים ב-state אבל יש deployStatus, אז כנראה יש כבר דיפלוימנט
        if (!deployStatus && !isLoading) {
          console.log('No existing deployment, starting new one automatically');
          await startDeployment(fileId);
          setDeploymentInitialized(true);
        } else if (deployStatus) {
          console.log('Existing deployment found, marking as initialized');
          setDeploymentInitialized(true);
          
          // אם כבר התחיל, הסתר את מסך הפתיחה
          if (deployStatus.startedAt) {
            setShowWelcomeCard(false);
          }
        }
      } catch (error) {
        console.error('Error in auto-initialization:', error);
        // לא נגדיר setError כדי לא להציג שגיאה למשתמש,
        // נאפשר לו להמשיך לדף הרגיל וללחוץ על כפתור ידנית
      }
    };
    
    // הפעל את האתחול האוטומטי רק אחרי שהמידע הראשוני מ-Firebase נטען
    if (!isLoading) {
      autoInitializeDeployment();
    }
  }, [fileId, deployStatus, isLoading, startDeployment, deploymentInitialized]);

  // טיפול במצב טעינה ראשוני ועדכוני סטטוס
  useEffect(() => {
    console.log('AppStatusLogic useEffect [isLoading, deployStatus] triggered.'); // LOG
    console.log('Current isLoading:', isLoading, 'DeployStatus:', deployStatus); // LOG
    if (!isLoading && deployStatus) {
      console.log('DeployStatus exists and not loading.'); // LOG
      
      // Check if there's a stage in progress to determine which card to show
      if (deployStatus.startedAt) {
        // Find the in-progress stage if any
        let inProgressIndex = -1;
        let nextIncompleteIndex = -1;
        
        if (cardTemplates && cardTemplates.length > 0) {
          const stageTemplates = cardTemplates.filter(card => card.type === 'stage');
          
          // First try to find a stage with in-progress status
          for (let i = 0; i < stageTemplates.length; i++) {
            const stageId = stageTemplates[i].id;
            if (deployStatus.stages && deployStatus.stages[stageId]?.status === 'in-progress') {
              inProgressIndex = i + 1; // +1 because index 0 is welcome card
              break;
            }
          }
          
          // If no in-progress stage, find the first incomplete stage
          if (inProgressIndex === -1) {
            for (let i = 0; i < stageTemplates.length; i++) {
              const stageId = stageTemplates[i].id;
              if (deployStatus.stages && deployStatus.stages[stageId]?.status !== 'completed') {
                nextIncompleteIndex = i + 1; // +1 because index 0 is welcome card
                break;
              }
            }
          }
        }
        
        // Only update cardIndex on initial load, not when stage status changes
        // This prevents automatic advancement when a stage is marked as completed
        if (!cardIndex || (!deployStatus.currentCardIndex && deployStatus.currentCardIndex !== 0)) {
          // Set card index to the in-progress stage, or next incomplete, or current from deployStatus
          if (inProgressIndex !== -1) {
            console.log(`Found in-progress stage at index ${inProgressIndex}. Setting card index to it.`);
            setCardIndex(inProgressIndex);
          } else if (nextIncompleteIndex !== -1) {
            console.log(`No in-progress stage found. Using next incomplete stage at index ${nextIncompleteIndex}.`);
            setCardIndex(nextIncompleteIndex);
          } else if (deployStatus.currentCardIndex !== undefined) {
            setCardIndex(deployStatus.currentCardIndex);
          }
        } else {
          // Never automatically update card index when stage status changes
          // Only allow the user to navigate using the Next/Prev buttons
          console.log('Not updating card index automatically - waiting for user to click navigation buttons');
        }
        
        setShowWelcomeCard(false); // Hide welcome card since process has started
      } else {
        // If process hasn't started, show welcome card
        setShowWelcomeCard(true);
        setCardIndex(0);
      }
    } else {
      console.log('Conditions not met to update local state from deployStatus.'); // LOG
    }
  }, [isLoading, deployStatus, cardTemplates]);

  // פונקציה להתחלת תהליך הדיפלוי (עדיין נשמרת עבור הכפתור)
  const handleStartDeployment = async () => {
    console.log('==== HANDLE START DEPLOYMENT CALLED ====');
    console.log('Current fileId from params:', fileId);
    
    try {
      setError('');
      // וודא שקיימות תבניות כרטיסים לפני התחלת התהליך
      if (!cardTemplates || cardTemplates.length === 0) {
        console.error('No card templates available to start deployment');
        setError('לא ניתן להתחיל את התהליך - תבניות כרטיסים חסרות');
        return;
      }
      
      console.log('Card templates available:', cardTemplates.length);
      
      // בדוק האם fileId הוא מחרוזת תקינה
      if (!fileId || typeof fileId !== 'string' || fileId.trim() === '') {
        console.error('Invalid fileId:', fileId);
        setError('מזהה קובץ לא תקין');
        return;
      }
      
      const appDeploymentsService = new AppDeploymentsService();
      
      // אם כבר יש לנו deployStatus (שיצרנו אוטומטית בטעינה)
      if (deployStatus && deployStatus.status === 'initialized') {
        console.log('Deployment already exists in "initialized" state. Updating to "in-progress"');
        
        // עדכן את הסטטוס ל-in-progress והגדר זמן התחלה
        const now = new Date().toISOString();
        await appDeploymentsService.updateDeployment(deployStatus.id, {
          status: 'in-progress',
          startedAt: now,
          updatedAt: now,
          lastInteractionAt: now
        });
        
        console.log('Updated deployment status to "in-progress"');
      } else if (!deployStatus) {
        // אם אין deployStatus בכלל, צור אחד חדש
        console.log('No deployment exists, creating a new one');
        await startDeployment(fileId);
      }
      
      setDeploymentInitialized(true);
      
      // עדכון UI מיידי
      setShowWelcomeCard(false); 
      
      console.log('Deployment started successfully from button click');
      console.log('==== HANDLE START DEPLOYMENT COMPLETED ====');
    } catch (error) {
      console.error('==== ERROR in handleStartDeployment ====');
      console.error('Error details:', error);
      console.error('Error stack:', error.stack);
      setError('התרחשה שגיאה בהתחלת תהליך הדיפלוי. אנא נסה שוב.');
    }
  };

  // טיפול בשמירת נתוני טופס זמניים - עדכון רק בסטייט מקומי
  const handleInputChange = useCallback((substepId, value) => {
    console.log('Saving temporary data for substep:', substepId);
    // This will now save to both local state and Firebase (debounced)
    saveTemporaryData(substepId, value);
  }, [saveTemporaryData]);

  // פונקציה לשמירת תשובה והמשך לשלב הבא (אם רלוונטי)
  const handleSubmitAnswer = async (stageId, substepId, answer) => {
    console.log('handleSubmitAnswer called for:', { stageId, substepId }); // LOG
    try {
      const success = await saveAnswer(stageId, substepId, answer);
      
      if (success) {
        console.log('Answer saved successfully for', substepId); // LOG
        // שמירת התשובה בסטייט מקומי (אופציונלי, תלוי אם משתמשים בזה)
        setCardResponses(prev => ({
          ...prev,
          [substepId]: answer
        }));
        
        // Check if this was the last substep to be completed for this stage
        // This will update the stage status in Firebase but won't automatically advance
        const currentStage = cardTemplates?.find(template => template.id === stageId);
        
        if (currentStage?.substeps && deployStatus?.stages?.[stageId]) {
          const allSubstepsCompleted = currentStage.substeps.every(
            substep => isSubstepCompleted(stageId, substep.id)
          );
          
          // If all substeps are completed, mark the stage as completed in Firebase
          // But don't automatically advance to the next card
          if (allSubstepsCompleted) {
            console.log('All substeps completed for stage:', stageId);
            
            const appDeploymentsService = new AppDeploymentsService();
            const now = new Date().toISOString();
            
            await appDeploymentsService.updateDeployment(deployStatus.id, {
              stages: {
                [stageId]: {
                  ...deployStatus.stages[stageId],
                  status: 'completed',
                  completedAt: now
                }
              },
              updatedAt: now,
              lastInteractionAt: now
            });
            
            console.log('Stage marked as completed in Firebase');
            // Don't navigate to the next card automatically - let user use the Next button
          }
        }
        
        return true;
      }
      console.error('Failed to save answer for', substepId); // LOG
      return false;
    } catch (error) {
      console.error('Error submitting answer:', error); // LOG
      setError('התרחשה שגיאה בשמירת התשובה. אנא נסה שוב.');
      return false;
    }
  };

  // פונקציה להשלמת כל התת-שלבים בשלב הנוכחי ומעבר לשלב הבא
  const handleStageCompletion = async () => {
    console.log('handleStageCompletion called for current index:', cardIndex); // LOG
    try {
      // כאן נבצע את הלוגיקה לסיום שלב
      // בדיקה שכל התת-שלבים מולאו (אם נדרש)

      console.log('Calling goToNextCard from handleStageCompletion...'); // LOG
      await goToNextCard(); // This should update deployStatus -> trigger useEffect -> update local cardIndex
      return true;
    } catch (error) {
      console.error('Error completing stage:', error); // LOG
      setError('התרחשה שגיאה בהשלמת השלב. אנא נסה שוב.');
      return false;
    }
  };

  // פונקציה לדילוג על שלב (למטרות פיתוח/דיבאג)
  const handleSkipStage = async () => {
    console.log('handleSkipStage (Next Card) called. Current local cardIndex:', cardIndex); // LOG
    try {
      // Check if current card is an information card
      if (cardTemplates && cardIndex < cardTemplates.length && cardTemplates[cardIndex]?.type === 'information') {
        console.log('Current card is an information card. Recording view timestamp.'); // LOG
        
        // Record that this information card was viewed
        const appDeploymentsService = new AppDeploymentsService();
        const infoCardId = cardTemplates[cardIndex].id;
        const now = new Date().toISOString();
        
        if (deployStatus?.id) {
          // Update the deployment to record that this information card was viewed
          await appDeploymentsService.updateDeployment(deployStatus.id, {
            informationViewed: {
              [infoCardId]: {
                viewedAt: now
              }
            },
            updatedAt: now,
            lastInteractionAt: now
          });
        }
      }
      
      console.log('Calling goToNextCard from handleSkipStage...'); // LOG
      const nextIndex = await goToNextCard(); // This updates firebase currentCardIndex
      
      // Immediately update local state to provide faster feedback
      if (nextIndex !== undefined) {
        // Only update cardIndex, don't trigger any other automatic behavior
        setCardIndex(nextIndex);
        console.log(`Manually setting cardIndex to ${nextIndex}`);
      } else {
        // If nextIndex is undefined, just increment the current index
        const newIndex = cardIndex + 1;
        if (newIndex < cardTemplates.length) {
          setCardIndex(newIndex);
          console.log(`No nextIndex returned, manually incrementing cardIndex to ${newIndex}`);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error skipping stage:', error); // LOG
      setError('התרחשה שגיאה בדילוג על השלב. אנא נסה שוב.');
      return false;
    }
  };

  // פונקציה לחזרה לשלב הקודם
  const handleGoBack = async () => {
    console.log('handleGoBack (Prev Card) called. Current local cardIndex:', cardIndex); // LOG
    try {
      if (cardIndex <= 1) {
        console.log('Already at first card, cannot go back further');
        return false;
      }
      
      // If we're going back to an information card, record that it was viewed again
      const prevIndex = cardIndex - 1;
      if (cardTemplates && prevIndex >= 0 && prevIndex < cardTemplates.length && 
          cardTemplates[prevIndex]?.type === 'information') {
        console.log('Previous card is an information card. Will record view timestamp.'); // LOG
        
        // Record that this information card was viewed
        const appDeploymentsService = new AppDeploymentsService();
        const infoCardId = cardTemplates[prevIndex].id;
        const now = new Date().toISOString();
        
        if (deployStatus?.id) {
          // Update the deployment to record that this information card was viewed
          await appDeploymentsService.updateDeployment(deployStatus.id, {
            informationViewed: {
              [infoCardId]: {
                viewedAt: now
              }
            },
            updatedAt: now,
            lastInteractionAt: now
          });
        }
      }
      
      console.log('Calling goToPrevCard from handleGoBack...'); // LOG
      await goToPrevCard(); // This updates firebase currentCardIndex
      
      // Immediately update local state to provide faster feedback
      setCardIndex(prevIndex);
      console.log(`Manually setting cardIndex to ${prevIndex}`);
      
      return true;
    } catch (error) {
      console.error('Error going back:', error); // LOG
      setError('התרחשה שגיאה בחזרה לשלב הקודם. אנא נסה שוב.');
      return false;
    }
  };

  // פונקציה להתחלת שלב תת-שאלות
  const handleStartSubsteps = async (stageId) => {
    console.log('handleStartSubsteps called for stageId:', stageId); // LOG
    try {
      // סימון שהשלב החל (לדוגמה, להצגת קומפוננטת השאלות) - שומר רק בסטייט מקומי
      // בעת לחיצה על "הבא" הנתונים יישמרו גם בפיירבייס
      console.log(`Saving temporary data: ${stageId}_started = true`); // LOG
      saveTemporaryData(`${stageId}_started`, true); 
      
      return true;
    } catch (error) {
      console.error('Error starting substeps:', error); // LOG
      setError('התרחשה שגיאה בהתחלת תת-השלבים. אנא נסה שוב.');
      return false;
    }
  };

  // בדיקה אם שלב הושלם
  const isStageCompleted = (stageId) => {
    if (!deployStatus || !deployStatus.stages) return false;
    const completed = deployStatus.stages[stageId]?.status === 'completed';
    return completed;
  };

  // בדיקה אם תת-שלב הושלם
  const isSubstepCompleted = (stageId, substepId) => {
    if (!deployStatus || !deployStatus.stages) return false;
    const completed = !!deployStatus.stages[stageId]?.answers?.[substepId];
    return completed;
  };

  // שליפת התקדמות קודמת
  const getSubstepValue = (substepId) => {
    // ראשית, בדוק אם יש נתון זמני מהסשן הנוכחי
    if (formData && formData[substepId] !== undefined) {
      return formData[substepId];
    }
    
    // אחרת בדוק אם יש תשובה שמורה בפיירבייס
    if (deployStatus?.stages) {
      for (const stage in deployStatus.stages) {
        if (deployStatus.stages[stage]?.answers?.[substepId]?.value !== undefined) {
          return deployStatus.stages[stage].answers[substepId].value;
        }
      }
    }
    
    return '';
  };

  // בדיקה אם שלב בתהליך כרגע
  const isCurrentStageInProgress = (stageId) => {
    if (!deployStatus || !deployStatus.stages) return false;
    return deployStatus.currentStage === stageId || 
           (deployStatus.stages[stageId]?.status === 'processing');
  };

  // Find the next stage that should be in progress (first incomplete stage)
  const getNextIncompleteStageIndex = () => {
    if (!cardTemplates || !deployStatus || !deployStatus.stages) return -1;
    
    const stageTemplates = cardTemplates.filter(card => card.type === 'stage');
    
    for (let i = 0; i < stageTemplates.length; i++) {
      const stageId = stageTemplates[i].id;
      if (deployStatus.stages[stageId]?.status !== 'completed') {
        return i + 1; // +1 because index 0 is welcome card
      }
    }
    
    return -1;
  };
  
  // Check if a specific stage is the next one that should be in progress
  const isNextIncompleteStage = (stageId) => {
    if (!cardTemplates || !deployStatus || !deployStatus.stages) return false;
    
    const stageTemplates = cardTemplates.filter(card => card.type === 'stage');
    
    // Find the first incomplete stage
    for (let i = 0; i < stageTemplates.length; i++) {
      const currentStageId = stageTemplates[i].id;
      if (deployStatus.stages[currentStageId]?.status !== 'completed') {
        // If this is the first incomplete stage and it matches our stageId
        return currentStageId === stageId;
      }
    }
    
    return false;
  };

  return {
    fileId,
    showWelcomeCard,
    error,
    cardIndex,
    cardResponses,
    deployStatus,
    cardTemplates,
    isLoading,
    formData,
    isSaving,
    lastSavedAt,
    showFullTimeline,
    setShowFullTimeline,
    handleStartDeployment,
    handleInputChange,
    handleSubmitAnswer,
    handleStageCompletion,
    handleSkipStage,
    handleGoBack,
    handleStartSubsteps,
    isStageCompleted,
    isSubstepCompleted,
    getSubstepValue,
    isCurrentStageInProgress,
    getNextIncompleteStageIndex,
    isNextIncompleteStage
  };
};

export default useAppStatusLogic; 